/**
 * Host Instructions Node - Player View
 *
 * Mission-briefing style instruction display for the host.
 * Non-host players see the host's avatar and a waiting state
 * with progress indication.
 */

import type { FlowContext } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { Check, FileText, Gamepad2, Map as MapIcon } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { PlayerAvatar } from "../../components/PlayerAvatar";
import { WaitingDots } from "../../components/WaitingDots";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type HostInstructionsState = {
  instructions: string[];
  hostConfirmed: boolean;
  hostUserId: string | null;
};

export function HostInstructionsView({
  nodeState,
  players,
  context,
  onAction,
}: NodePlayerViewProps<HostInstructionsState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedSteps, setCheckedSteps] = useState<Set<number>>(new Set());

  const state = nodeState.state as HostInstructionsState;
  const config = nodeState.config as { labels?: { title?: string; subtitle?: string } };
  const isHost = state.hostUserId === userId;
  const hostPlayer = players.find((p) => p.userId === state.hostUserId);
  const allChecked = checkedSteps.size === state.instructions.length;

  const title = config.labels?.title ?? (isHost ? "Mission Briefing" : "Lobby Setup");
  const subtitle =
    config.labels?.subtitle ??
    (isHost
      ? "Complete these steps to set up the match"
      : `${hostPlayer?.username ?? "Host"} is setting up the lobby`);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError(null);

    const result = await onAction("confirm", { userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  const toggleStep = (index: number) => {
    setCheckedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <NodeCard>
      <NodeHeader title={title} subtitle={subtitle} />

      {/* Match details from context — map, game mode, server */}
      <MatchDetailsPills context={context} />

      {/* Host avatar badge — shown to non-host players */}
      {!isHost && hostPlayer && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flex justify-center mb-6"
        >
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-surface-800/80 border border-surface-700/50">
            <div className="relative">
              <PlayerAvatar
                avatarUrl={hostPlayer.avatarUrl}
                username={hostPlayer.username}
                size="lg"
                ringColor="ring-amber-400/50"
              />
              {!state.hostConfirmed && (
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-400 border-2 border-surface-900 animate-pulse" />
              )}
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-text-primary">{hostPlayer.username}</p>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-display font-semibold uppercase tracking-wider text-amber-400">
                  Host
                </span>
                {!state.hostConfirmed && (
                  <span className="text-[10px] text-surface-500">Setting up...</span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Instructions list */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="max-w-lg mx-auto mb-6"
      >
        <div className="rounded-lg bg-surface-800/60 border border-surface-700/40 overflow-hidden">
          {/* Instructions header bar */}
          <div className="px-4 py-2.5 border-b border-surface-700/40 flex items-center gap-2">
            <FileText size={14} className="text-brand-400/70" aria-hidden="true" />
            <span className="text-xs font-display font-semibold uppercase tracking-wider text-text-secondary">
              Setup Instructions
            </span>
            <div className="flex-1" />
            <span className="text-xs text-surface-500 tabular-nums">
              {isHost ? (
                <>
                  <span className="text-brand-400 font-semibold">{checkedSteps.size}</span>/
                  {state.instructions.length}
                </>
              ) : (
                <>{state.instructions.length} steps</>
              )}
            </span>
          </div>

          {/* Steps */}
          <div className="divide-y divide-surface-700/20">
            {state.instructions.map((instruction, index) => {
              const isChecked = checkedSteps.has(index);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 + index * 0.06 }}
                >
                  {isHost && !state.hostConfirmed ? (
                    <button
                      onClick={() => toggleStep(index)}
                      className={cn(
                        "w-full flex items-start gap-3 px-4 py-3 text-left transition-colors duration-200 cursor-pointer",
                        "hover:bg-surface-700/20",
                        "focus:outline-none focus:bg-surface-700/30",
                        isChecked && "bg-brand-500/[0.04]",
                      )}
                    >
                      <StepCheckbox checked={isChecked} index={index} />
                      <p
                        className={cn(
                          "text-sm transition-colors duration-200 pt-0.5",
                          isChecked
                            ? "text-text-secondary line-through decoration-surface-600"
                            : "text-text-primary",
                        )}
                      >
                        {instruction}
                      </p>
                    </button>
                  ) : (
                    <div className="flex items-start gap-3 px-4 py-3">
                      <StepNumber index={index} confirmed={state.hostConfirmed} />
                      <p
                        className={cn(
                          "text-sm pt-0.5",
                          state.hostConfirmed ? "text-text-secondary" : "text-text-primary",
                        )}
                      >
                        {instruction}
                      </p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>

      <ErrorBanner error={error} />

      {/* Host confirm button */}
      {isHost && !state.hostConfirmed && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="flex justify-center"
        >
          <button
            onClick={handleConfirm}
            disabled={isSubmitting || !allChecked}
            className={cn(
              "relative px-8 py-3 rounded-sm font-bold text-sm uppercase tracking-wider",
              "transition-all duration-200 cursor-pointer",
              "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              allChecked
                ? "bg-brand-500 hover:bg-brand-600 text-surface-900 shadow-[0_0_20px_rgba(38,217,181,0.2)]"
                : "bg-surface-700 text-surface-400",
            )}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 border-2 border-surface-900/30 border-t-surface-900 rounded-full animate-spin" />
                Confirming...
              </span>
            ) : allChecked ? (
              "I've Completed Setup"
            ) : (
              `Check all ${state.instructions.length} steps to continue`
            )}
          </button>
        </motion.div>
      )}

      {/* Confirmed state */}
      <AnimatePresence>
        {state.hostConfirmed && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="flex justify-center"
          >
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-lg bg-brand-500/10 border border-brand-500/20">
              <div className="w-5 h-5 rounded-full bg-brand-500/20 flex items-center justify-center">
                <motion.svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-brand-400"
                  aria-hidden="true"
                >
                  <motion.path
                    d="M5 13l4 4L19 7"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                  />
                </motion.svg>
              </div>
              <span className="text-sm font-medium text-brand-400">Setup complete</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Non-host waiting indicator */}
      {!isHost && !state.hostConfirmed && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col items-center gap-2"
        >
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-surface-800/60 border border-surface-700/40">
            <WaitingDots />
            <span className="text-sm text-text-secondary">Waiting for host to finish setup</span>
          </div>
          {hostPlayer?.isConnected === false && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-error-500/10 border border-error-500/20">
              <div className="w-1.5 h-1.5 rounded-full bg-error-400 animate-pulse" />
              <span className="text-xs text-error-400">
                Host is offline — waiting for reconnection or timeout
              </span>
            </div>
          )}
        </motion.div>
      )}
    </NodeCard>
  );
}

// ---------------------------------------------------------------------------
// Step checkbox (host view, interactive)
// ---------------------------------------------------------------------------

function StepCheckbox({ checked, index }: { checked: boolean; index: number }) {
  return (
    <div
      className={cn(
        "shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200",
        checked ? "bg-brand-500 border-brand-500" : "border-surface-500/50 bg-transparent",
      )}
    >
      <AnimatePresence>
        {checked && (
          <motion.div
            key={`check-${index}`}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
            <Check size={10} strokeWidth={3} className="text-surface-900" aria-hidden="true" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step number (non-host view / confirmed view)
// ---------------------------------------------------------------------------

function StepNumber({ index, confirmed }: { index: number; confirmed: boolean }) {
  return (
    <div
      className={cn(
        "shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold",
        confirmed ? "bg-brand-500/15 text-brand-400/60" : "bg-brand-500/15 text-brand-400",
      )}
    >
      {confirmed ? (
        <Check size={10} strokeWidth={3} className="text-brand-400/60" aria-hidden="true" />
      ) : (
        index + 1
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Match details pills (map, game mode, server from flow context)
// ---------------------------------------------------------------------------

function resolveDisplayName(val: unknown): string | null {
  if (val == null) return null;
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object") {
    const name = (val as Record<string, unknown>).name;
    if (typeof name === "string") return name;
    if (typeof name === "number" || typeof name === "boolean") return String(name);
    return JSON.stringify(val);
  }
  return null;
}

function MatchDetailsPills({ context }: { context: FlowContext }) {
  const mapNames = context.mapNames;
  const rawMapId = resolveDisplayName(context.selectedMapId ?? context.selectedMap);
  const map =
    resolveDisplayName(context.selectedMapName) ?? (rawMapId && mapNames?.[rawMapId]) ?? rawMapId;
  const gameMode = resolveDisplayName(context.selectedGameMode ?? context.selectedGameModeId);

  const pills: { icon: typeof MapIcon; label: string; value: string }[] = [];
  if (map) pills.push({ icon: MapIcon, label: "Map", value: map });
  if (gameMode) pills.push({ icon: Gamepad2, label: "Mode", value: gameMode });

  if (pills.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="flex flex-wrap items-center justify-center gap-2 mb-6"
    >
      {pills.map(({ icon: Icon, label, value }) => (
        <div
          key={label}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-800/80 border border-surface-700/50"
        >
          <Icon className="size-3.5 text-brand-400/70" />
          <span className="text-[10px] font-medium uppercase tracking-wider text-surface-500">
            {label}
          </span>
          <span className="text-sm font-medium text-text-primary">{value}</span>
        </div>
      ))}
    </motion.div>
  );
}
